// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-AUDIT-2 — TanStack Query hooks for the audit search (E-02: components never call api.* directly).
// Every key carries the scope, so a SaaS admin switching pharmacy can never be shown the previous
// pharmacy's cached rows.

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import * as api from '../../api/client'

export const auditKeys = {
  search: (a: api.AuditSearchArgs) => ['audit', 'search', a.pharmacyId ?? 'bound', a] as const,
  entry: (id: number, pharmacyId?: string) => ['audit', 'entry', pharmacyId ?? 'bound', id] as const,
  catalog: (pharmacyId?: string) => ['audit', 'catalog', pharmacyId ?? 'bound'] as const,
  subjects: (q: string, pharmacyId?: string) => ['audit', 'subjects', pharmacyId ?? 'bound', q] as const,
}

/** One page of the search. Disabled until a scope exists (SaaS/support must pick a pharmacy). */
export function useAuditSearch(args: api.AuditSearchArgs, enabled: boolean) {
  return useQuery({
    queryKey: auditKeys.search(args),
    queryFn: () => api.searchAudit(args),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  })
}

export function useAuditEntry(id: number | null, pharmacyId: string | undefined) {
  return useQuery({
    queryKey: auditKeys.entry(id ?? 0, pharmacyId),
    queryFn: () => api.getAuditEntry(id!, pharmacyId),
    enabled: id !== null,
    staleTime: 60_000,
  })
}

export function useAuditCatalog(pharmacyId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: auditKeys.catalog(pharmacyId),
    queryFn: () => api.getAuditCatalog(pharmacyId),
    enabled,
    staleTime: 10 * 60_000,
  })
}

/** Patient / Rx lookup for the "About" filter. Runs from 2 characters. */
export function useAuditSubjects(q: string, pharmacyId: string | undefined, enabled: boolean) {
  const term = q.trim()
  return useQuery({
    queryKey: auditKeys.subjects(term, pharmacyId),
    queryFn: () => api.lookupAuditSubjects(term, pharmacyId),
    enabled: enabled && term.length >= 2,
    staleTime: 30_000,
  })
}

/** Pharmacies for the SaaS / support scope picker. */
export function useAuditPharmacies(enabled: boolean) {
  return useQuery({
    queryKey: ['audit', 'pharmacies'] as const,
    queryFn: () => api.listPharmacies(),
    enabled,
    staleTime: 5 * 60_000,
  })
}
